import { useState } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const services = [
  {
    icon: 'ri-brain-line',
    title: 'Inteligência Artificial',
    shortDesc: 'Modelos de ML, LLMs, chatbots e automação inteligente para o seu negócio.',
    details: 'Desenvolvemos e integramos soluções de IA sob medida: modelos preditivos, processamento de linguagem natural, visão computacional, chatbots com LLMs e muito mais. Transformamos dados em decisões inteligentes.',
    color: 'emerald',
  },
  {
    icon: 'ri-code-s-slash-line',
    title: 'Desenvolvimento de Software',
    shortDesc: 'Sistemas web, mobile e APIs robustas, escaláveis e bem arquitetadas.',
    details: 'Criamos aplicações sob medida com as melhores práticas de engenharia de software: desde MVPs ágeis até sistemas complexos, sempre com foco em qualidade, desempenho e manutenibilidade.',
    color: 'teal',
  },
  {
    icon: 'ri-presentation-line',
    title: 'Consultoria Tecnológica',
    shortDesc: 'Diagnóstico, planejamento e execução da transformação digital da sua empresa.',
    details: 'Mergulhamos no seu modelo de negócio para identificar oportunidades e gargalos tecnológicos. Entregamos um roadmap claro, viável e orientado a resultados para sua transformação digital.',
    color: 'emerald',
  },
  {
    icon: 'ri-bar-chart-box-line',
    title: 'Análise de Dados & BI',
    shortDesc: 'Dashboards, relatórios e insights que guiam decisões estratégicas.',
    details: 'Coletamos, integramos e visualizamos dados de múltiplas fontes. Criamos dashboards interativos, pipelines de dados e análises avançadas para que você tome decisões com clareza e confiança.',
    color: 'teal',
  },
  {
    icon: 'ri-robot-line',
    title: 'Automação de Processos',
    shortDesc: 'RPA, integrações e fluxos automatizados para ganhar tempo e escala.',
    details: 'Identificamos e automatizamos tarefas repetitivas e processos manuais com RPA, APIs e integrações inteligentes. Sua equipe foca no que realmente importa enquanto as máquinas trabalham por você.',
    color: 'emerald',
  },
  {
    icon: 'ri-cloud-line',
    title: 'Cloud & Infraestrutura',
    shortDesc: 'Arquiteturas cloud modernas na AWS, Azure e GCP, seguras e econômicas.',
    details: 'Projetamos e gerenciamos infraestruturas cloud escaláveis, seguras e com custo otimizado. Desde a migração até DevOps e monitoramento contínuo, sua operação fica estável e eficiente.',
    color: 'teal',
  },
];

export default function ServicesSection() {
  const { ref, visible } = useScrollAnimation();
  const [activeCard, setActiveCard] = useState<number | null>(null);

  return (
    <section id="services" className="py-20 md:py-28 bg-zinc-50">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div ref={ref} className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="inline-block text-emerald-600 font-semibold text-sm font-inter tracking-widest uppercase mb-3">O que fazemos</span>
          <h2 className="font-sora font-bold text-3xl md:text-4xl text-zinc-900 mb-4">Nossos Serviços</h2>
          <p className="font-inter text-zinc-500 max-w-xl mx-auto text-base">
            Soluções completas que cobrem todo o ciclo da transformação digital, desde a estratégia até a implementação e evolução contínua.
          </p>
          <div className="w-12 h-1 bg-emerald-500 rounded-full mx-auto mt-5" />
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((s, i) => (
            <div
              key={s.title}
              className={`group rounded-xl bg-white border cursor-pointer transition-all duration-500 overflow-hidden ${
                activeCard === i
                  ? 'border-emerald-400 -translate-y-1'
                  : 'border-zinc-100 hover:border-emerald-200 hover:-translate-y-1'
              } ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: visible ? `${i * 80}ms` : '0ms' }}
              onClick={() => setActiveCard(activeCard === i ? null : i)}
            >
              <div className="p-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-emerald-50 mb-4 group-hover:bg-emerald-100 transition-colors">
                  <i className={`${s.icon} text-emerald-600 text-2xl`} />
                </div>
                <h3 className="font-sora font-semibold text-zinc-900 text-base mb-2">{s.title}</h3>
                <p className="font-inter text-zinc-500 text-sm leading-relaxed">{s.shortDesc}</p>
              </div>

              {/* Expandable detail */}
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  activeCard === i ? 'max-h-40' : 'max-h-0'
                }`}
              >
                <div className="px-6 pb-6 border-t border-zinc-100">
                  <p className="font-inter text-zinc-600 text-sm leading-relaxed mt-4">{s.details}</p>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 pb-5 flex items-center justify-between">
                <span className="text-emerald-600 text-xs font-semibold font-inter">
                  {activeCard === i ? 'Fechar ↑' : 'Saiba mais ↓'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
